import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DataFileInterface, FileSourceEnum } from '@systemeio/file-manager'
import React, { ReactNode, forwardRef, useState } from 'react'
import { ImageUploader } from 'shared/components/file-uploader/image-uploader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useWindowSize from 'shared/hooks/use-window-size'
import { TrashIcon } from '@systemeio/ui/icons'

interface ProductMediaLibraryProps {
  images: DataFileInterface[]
  setImages: React.Dispatch<React.SetStateAction<DataFileInterface[]>>
}

function ProductMediaLibrary({ images, setImages }: ProductMediaLibraryProps) {
  const [showImageCount, setShowImageCount] = useState(4)
  const { width } = useWindowSize()
  const { t } = useLocoTranslation()
  const isMobile = width < 768

  const sortableImages = images.map(image => {
    return { ...image, id: image.path }
  })
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  function onCoverUploadSuccess(dataFile: DataFileInterface) {
    setImages(prev => [...prev, dataFile])
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setImages(images => {
        const oldIndex = images.findIndex(image => image.path === String(active.id))
        const newIndex = images.findIndex(image => image.path === String(over.id))

        return arrayMove(images, oldIndex, newIndex)
      })
    }

    setActiveId(null)
  }

  function handleDragCancel() {
    setActiveId(null)
  }

  function handleDeleteImage(url: UniqueIdentifier) {
    setImages(prev => prev.filter(image => image.path !== url))
  }

  return (
    <>
      {!!images.length ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={sortableImages} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-[repeat(2,1fr)]  gap-2 p-[10px] md:grid-cols-[repeat(4,1fr)]">
              {isMobile
                ? images
                    .slice(0, showImageCount)
                    .map((image, index) => (
                      <SortablePhoto
                        key={image.path}
                        url={image.path}
                        index={index}
                        deleteImage={handleDeleteImage}
                      />
                    ))
                : images.map((image, index) => (
                    <SortablePhoto
                      key={image.path}
                      url={image.path}
                      index={index}
                      deleteImage={handleDeleteImage}
                    />
                  ))}
              <ImageUploader
                imageCropperModalProps={{ imageCropperProps: { aspectRatio: 1 } }}
                dropZoneClassName="mt-0 p-0 w-full h-full min-h-[80px] lg:min-h-[120px]"
                onSelect={onCoverUploadSuccess}
                source={FileSourceEnum.Product}
              />
            </div>
          </SortableContext>
          {showImageCount < images.length && (
            <div
              className="flex items-center justify-center"
              onClick={() => setShowImageCount(prev => prev + 4)}
            >
              {t('dashboard.products.media.load_more_label')}
            </div>
          )}
          <DragOverlay adjustScale={true}>
            {activeId ? (
              <div
                className="relative"
                style={getImageContainerStyles(
                  images.findIndex(image => image.path === String(activeId)),
                )}
              >
                <TrashIcon
                  className="fill-brand-primarygray hover:fill-danger absolute right-2 top-2 cursor-pointer"
                  width="14px"
                  onClick={() => handleDeleteImage(activeId)}
                />
                <Photo
                  url={activeId}
                  index={images.findIndex(image => image.path === String(activeId))}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <ImageUploader
          imageCropperModalProps={{ imageCropperProps: { aspectRatio: 1 } }}
          dropZoneClassName="mt-0 p-0 w-full h-full min-h-[80px] lg:min-h-[120px]"
          onSelect={onCoverUploadSuccess}
          source={FileSourceEnum.Product}
        />
      )}
    </>
  )
}

interface PhotoProps {
  children?: ReactNode
  url: UniqueIdentifier
  index: number
  style?: Record<string, any>
  isDragging?: boolean
}

type Ref = HTMLDivElement

const Photo = forwardRef<Ref, PhotoProps>(({ url, index, style, isDragging, ...props }, ref) => {
  const inlineStyles = {
    gridRowStart: index === 0 ? 'span 2' : undefined,
    gridColumnStart: index === 0 ? 'span 2' : undefined,
    backgroundImage: isDragging ? 'none' : `url("${url}")`,
    backgroundColor: isDragging ? 'gray' : 'rgb(var(--background-secondary))',
    ...style,
  }

  return (
    <div
      ref={ref}
      className={`border-[rgb(var(--text-secondary-400))] origin-[0px_0px] rounded-sm border-2 bg-contain bg-center bg-no-repeat pb-[100%]`}
      style={inlineStyles}
      {...props}
    />
  )
})

Photo.displayName = 'Photo'

const getImageContainerStyles = (index?: UniqueIdentifier) => {
  return {
    gridRowStart: index === 0 ? 'span 2' : undefined,
    gridColumnStart: index === 0 ? 'span 2' : undefined,
  }
}

interface SortablePhotoProps {
  url: UniqueIdentifier
  index: number
  deleteImage: (value: UniqueIdentifier) => void
}

const SortablePhoto = ({ url, index, deleteImage }: SortablePhotoProps) => {
  const sortable = useSortable({ id: url })
  const { attributes, listeners, isDragging, setNodeRef, transform, transition } = sortable

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div className="relative" style={getImageContainerStyles(index)}>
      <TrashIcon
        className="fill-brand-primarygray hover:fill-danger absolute right-2 top-2 cursor-pointer"
        width="14px"
        onClick={() => deleteImage(url)}
      />
      <Photo
        ref={setNodeRef}
        style={style}
        url={url}
        index={index}
        isDragging={isDragging}
        {...attributes}
        {...listeners}
      />
    </div>
  )
}

export default ProductMediaLibrary
